import React, { forwardRef, useCallback, useMemo } from "react";
import { Box, Typography, Stack, Divider, MenuItem } from "@mui/material";
import { GlassPanel, ModernTextField } from "./styledComponents";

const DATE_FIELD_WIDTH = { xs: "100%", md: "50%" };
const VALUE_FIELD_WIDTH = { xs: "66.6667%", md: "41.6667%" };
const UNIT_FIELD_WIDTH = { xs: "33.3333%", md: "25%" };

const MEASUREMENT_GROUPS = [
  {
    title: "Mediciones principales",
    description:
      "Ingresa los valores que registras de forma habitual en tu consulta.",
    fields: [
      {
        label: "Circunferencia de cintura",
        name: "circunferenciaCintura",
        helper: "Dato clave para seguimiento abdominal.",
        units: ["cm", "in"],
      },
      {
        label: "Porcentaje de grasa",
        name: "porcentajeGrasa",
        helper: "Introduce el porcentaje reportado por tu medición.",
        units: ["%"],
      },
      {
        label: "Porcentaje de agua",
        name: "porcentajeAgua",
        helper: "Ideal para validar hidratación corporal.",
        units: ["%"],
      },
      {
        label: "Porcentaje de músculo",
        name: "porcentajeMusculo",
        helper: "Útil para ver progreso de entrenamiento.",
        units: ["%"],
      },
    ],
  },
  {
    title: "Indicadores complementarios",
    description:
      "Añade los valores con los que interpretas la evolución metabólica.",
    fields: [
      {
        label: "DCI / Edad de digestión",
        name: "edadDigestion",
        helper: "Comparación entre metabolismo y edad cronológica.",
        units: ["años"],
      },
      {
        label: "VISC / Grasa visceral",
        name: "grasaVisceral",
        helper: "Manténlo por debajo del rango de riesgo que uses.",
        units: ["nivel"],
      },
    ],
  },
];

const IndicesCorporales = forwardRef(({ formValues, onChange }, ref) => {
  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      onChange(name, value);
    },
    [onChange]
  );

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  return (
    <Box
      sx={{
        padding: { xs: 2, md: 3 },
        backgroundColor: (theme) => theme.palette.background.default,
        borderRadius: 4,
      }}
    >
      <GlassPanel
        sx={{
          mb: 3,
          background: (theme) => theme.palette.background.paper,
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="overline" sx={{ letterSpacing: 3 }}>
            Registro especializado
          </Typography>
          <Typography variant="h5" fontWeight={800} color="primary.main">
            Índices corporales
          </Typography>
          <Typography color="text.secondary">
            Centraliza los datos que complementan el seguimiento clínico. Esta
            sección permite comparar tendencias en grasa, músculo e
            interpretaciones avanzadas del equipo de nutrición.
          </Typography>
        </Stack>
      </GlassPanel>

      <GlassPanel
        sx={{ background: (theme) => theme.palette.background.paper }}
      >
        <Stack spacing={4}>
          <ModernTextField
            label="Fecha del estudio"
            name="fechaIndices"
            value={formValues.fechaIndices || ""}
            onChange={handleInputChange}
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: today }}
            helperText="Ingresa la fecha en que se tomó la medición"
            sx={{
              width: DATE_FIELD_WIDTH,
              "& input[type='date']::-webkit-calendar-picker-indicator": {
                cursor: "pointer",
              },
            }}
          />

          {MEASUREMENT_GROUPS.map((group, index) => (
            <Stack key={group.title} spacing={2.5}>
              <Box>
                <Typography variant="subtitle2" color="primary.main">
                  {group.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {group.description}
                </Typography>
              </Box>

              {group.fields.map((field) => (
                <Stack key={field.name} spacing={1.25}>
                  <Typography variant="body2" color="text.secondary">
                    {field.label}
                  </Typography>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    sx={{ flexWrap: "wrap" }}
                  >
                    <ModernTextField
                      label="Valor"
                      name={field.name}
                      type="number"
                      value={formValues[field.name] || ""}
                      onChange={handleInputChange}
                      fullWidth
                      sx={{ width: VALUE_FIELD_WIDTH }}
                      helperText={field.helper}
                    />
                    <ModernTextField
                      select
                      label="Unidad"
                      name={`${field.name}Medida`}
                      value={
                        formValues[`${field.name}Medida`] ||
                        field.units[0] ||
                        ""
                      }
                      onChange={handleInputChange}
                      fullWidth
                      sx={{ width: UNIT_FIELD_WIDTH }}
                    >
                      {field.units.map((unit) => (
                        <MenuItem key={unit} value={unit}>
                          {unit}
                        </MenuItem>
                      ))}
                    </ModernTextField>
                  </Stack>
                </Stack>
              ))}

              {index < MEASUREMENT_GROUPS.length - 1 && <Divider />}
            </Stack>
          ))}
        </Stack>
      </GlassPanel>
    </Box>
  );
});

export default IndicesCorporales;
